// Fact Find: the routes and the sidebar rows of `wealth.onboarding.fact-find` (ADR-0027).
import type { ClientDetail } from '@septima/contract';
import { defineWebFeature } from '@/features/kit';

/**
 * The page behind the fact find status of this client, or undefined. The
 * Desk opens the fact find of the client. The register would list every
 * client, and its freshness can differ from the status of this record. Ops
 * and Comply have no fact find register, so they open the suitability record.
 */
function factFindLink(detail: ClientDetail, view: string): string | undefined {
    const factFind = detail.factFinds[0];
    if (view === 'desk') return factFind ? `/desk/fact-find/${factFind.id}` : '/desk/fact-find';
    const record = detail.suitabilityRecords[0];
    if (record && (view === 'ops' || view === 'comply')) {
        return `/${view}/suitability-records/${record.recordId}`;
    }
    return undefined;
}

export default defineWebFeature({
    id: 'wealth.onboarding.fact-find',
    routes: [
        { frontend: 'desk', path: 'fact-find', lazy: () => import('@/pages/desk/fact-find/page') },
        {
            frontend: 'desk',
            path: 'fact-find/:factFindId',
            lazy: () => import('@/pages/desk/fact-find/client/page'),
        },
        {
            frontend: 'desk',
            path: 'fact-find/:factFindId/refresh',
            lazy: () => import('@/pages/desk/fact-find/client/refresh'),
        },
        // Ops and Compliance open a fact find from its record, read-only.
        {
            frontend: 'ops',
            path: 'fact-find/:factFindId',
            lazy: () => import('@/pages/ops/fact-find/client/page'),
        },
        {
            frontend: 'comply',
            path: 'fact-find',
            lazy: () => import('@/pages/comply/fact-find/page'),
        },
        {
            frontend: 'comply',
            path: 'fact-find/:factFindId',
            lazy: () => import('@/pages/comply/fact-find/client/page'),
        },
    ],
    nav: [
        // --- Advice & Suitability ---
        // The fact-find register + "Create fact find" (Individual / Entity)
        // entry point and the per-client prefill / risk / signing workbench
        // (fact-find pipeline). This is the canonical "Fact Find" surface: it
        // holds the fact finds with their PDF / signing arrangements, so it
        // takes the "Fact Find" label and slot. The older client-reviews
        // register (below, hidden) embeds the same fact-find document per
        // client but has no create-from-scratch affordance.
        {
            frontend: 'desk',
            key: 'fact-find',
            label: 'Fact Find',
            href: '/desk/fact-find',
            icon: 'FileText',
            group: 'advice-suitability',
            order: 410,
        },
        // --- Consumer Duty ---
        // The read of every fact find of the firm. Compliance checks that each
        // IM and each assistant submits them correctly, and changes nothing.
        {
            frontend: 'comply',
            key: 'fact-find',
            label: 'Fact Find',
            href: '/comply/fact-find',
            icon: 'FileText',
            group: 'consumer-duty',
            order: 225,
        },
    ],
    slots: {
        'client.detail.fact-find-link': [
            {
                key: 'fact-find',
                order: 10,
                select: (_input, ctx) => factFindLink(ctx.detail, ctx.view),
            },
        ],
        'client.detail.attention': [
            {
                key: 'fact-find',
                order: 10,
                select: (_input, ctx) => {
                    const status = ctx.detail.identity.complianceStatus;
                    if (status !== 'Overdue' && status !== 'Review Approaching') return null;
                    return {
                        key: 'fact-find',
                        label: `Fact find ${status === 'Overdue' ? 'overdue' : 'review approaching'}`,
                        tone: status === 'Overdue' ? 'negative' : 'warning',
                        to: factFindLink(ctx.detail, ctx.view),
                    };
                },
            },
        ],
    },
});
